import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BottomSheet } from '../ui/states/BottomSheet'
import { HELP_VIDEOS, posterSrc, videoSrc } from '../../lib/helpVideos'
import { track } from '../../lib/track'
import { useHelpSheet } from '../../stores/helpSheetStore'

/**
 * Bottom-sheet video player for the help center. Mounted once app-wide (App.tsx)
 * and driven by `useHelpSheet` so any surface — the Help list, the onboarding
 * row, or a contextual hint dot — can pop it. Renders nothing until a video is
 * open, so it's DOM-inert (and flag-inert) when idle.
 *
 * Media: real clips play inline via <video> (poster still, controls, no
 * preload). Until a clip is filmed (`available:false`) the sheet shows a
 * dignified "coming soon" plate over the same tinted gradient — the row copy
 * already sells the topic, so this stays a promise, not an apology.
 *
 * Analytics: `help_video_played {id, from}` on the first real playback start
 * (the <video> 'play' event — never on open, never for coming-soon),
 * `help_video_completed {id}` on 'ended', and `help_feedback {id, helpful}` on
 * the one Yes/Not-quite vote per open.
 */
export function HelpVideoSheet() {
  const openId = useHelpSheet((s) => s.openId)
  const from = useHelpSheet((s) => s.from)
  const close = useHelpSheet((s) => s.close)
  const video = openId ? HELP_VIDEOS.find((v) => v.id === openId) ?? null : null

  // One vote + one play-event per open. Reset whenever the open video changes.
  const [vote, setVote] = useState<boolean | null>(null)
  const playedRef = useRef(false)
  useEffect(() => {
    setVote(null)
    playedRef.current = false
  }, [openId])

  // Route awareness: this sheet lives app-wide (App.tsx), so unlike a
  // component-local sheet it does not die with the page it was opened over.
  // Close it whenever the route path changes while a video is open, so a
  // browser/hardware Back (or any in-app nav) can't strand the panel + its
  // z-50 backdrop on top of a different page. Keyed on pathname only, so
  // search/hash-only changes never dismiss the player.
  const { pathname } = useLocation()
  const openIdRef = useRef(openId)
  openIdRef.current = openId
  const firstPathRef = useRef(true)
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false
      return
    }
    if (openIdRef.current) close()
  }, [pathname, close])

  return (
    <BottomSheet
      open={!!video}
      onClose={close}
      panelClassName="rounded-t-[20px] p-0"
      ariaLabel={video ? `${video.title} — help video` : undefined}
    >
      {video && (
        <div className="px-[18px] pb-1 pt-1">
          {/* Media area — real clip when filmed, else a coming-soon plate.
              Portrait 9:16: the clips are phone screen recordings, so a 16:9
              box would letterbox them into a sliver.

              Height, not width, is the driving dimension (the aspect ratio then
              derives the width). The sheet panel is max-h-[80vh] — proportional
              — but everything below the video (title, meta, the feedback row,
              and the panel's own padding) is a CONSTANT ~236px, measured
              identical at 390x844, 375x667 and 360x640. A fixed `vh` height is
              therefore only correct on one screen: at 58vh the feedback row
              fell off the bottom of an iPhone SE. Subtracting the chrome keeps
              the whole sheet on screen at every height.
              15.5rem = 248px = the measured 236px + ~12px of slack.
              max-w-full guards very narrow viewports; object-contain then keeps
              the frame honest rather than stretching it. */}
          <div
            className="mx-auto flex aspect-[9/16] h-[calc(80vh-15.5rem)] max-w-full items-center justify-center overflow-hidden rounded-xl border border-hairline"
            style={{
              background:
                'linear-gradient(135deg, var(--violet-bg), transparent), var(--color-bg-raised)',
            }}
          >
            {video.available ? (
              <video
                className="h-full w-full object-contain"
                playsInline
                preload="none"
                controls
                poster={posterSrc(video.id)}
                onPlay={() => {
                  if (playedRef.current) return
                  playedRef.current = true
                  track('help_video_played', { id: video.id, from })
                }}
                onEnded={() => track('help_video_completed', { id: video.id })}
              >
                <source src={videoSrc(video.id)} type="video/mp4" />
              </video>
            ) : (
              <div className="flex flex-col items-center gap-2 px-6 text-center">
                <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-accent-border bg-bg-card pl-1 text-base text-accent-fg">
                  ▶
                </span>
                <span className="font-body text-[0.72rem] tracking-[0.08em] text-text-secondary">
                  Video coming soon
                </span>
              </div>
            )}
          </div>

          {/* Title + meta */}
          <div className="mt-3.5">
            <div className="font-display text-lg font-light text-text-primary">{video.title}</div>
            <div className="mt-1 text-[0.7rem] text-text-secondary">
              {video.sub} · {video.dur}
            </div>
          </div>

          {/* Feedback */}
          <div className="mt-4 border-t border-hairline pt-3">
            {vote === null ? (
              <div className="flex items-center gap-2.5">
                <span className="flex-1 text-[0.7rem] text-text-secondary">Was this helpful?</span>
                {[
                  { label: 'Yes', helpful: true },
                  { label: 'Not quite', helpful: false },
                ].map(({ label, helpful }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setVote(helpful)
                      track('help_feedback', { id: video.id, helpful })
                    }}
                    className="rounded-full border border-hairline-strong bg-transparent px-3 py-1.5 font-body text-[0.72rem] text-text-secondary transition-colors hover:border-accent-border hover:text-accent-fg"
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : vote ? (
              <p className="text-[0.72rem] text-text-secondary">
                Thank you — glad it landed. ✦
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                <p className="text-[0.72rem] text-text-secondary">
                  Thanks for telling us — we'll make it clearer.
                </p>
                <Link
                  to="/beta-feedback"
                  onClick={close}
                  className="text-[0.72rem] font-medium text-accent-fg underline underline-offset-2"
                >
                  Tell us what you needed
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </BottomSheet>
  )
}
